import {Component, EventEmitter, Input, OnInit, Output} from '@angular/core';
import {WidgetService} from '../../../../../../../domain/widget/service/widget.service';
import {SafeSubscriberDirective} from '../../../../../../../application/app-context/safe-subscriber.directive';
import {WidgetDetail} from '../../../../../../../domain/widget/model/widget-detail';
import {TranslateService} from '@ngx-translate/core';
import * as Highcharts from 'highcharts';
import {Chart} from 'angular-highcharts';
import {SimulationImpact} from '../../../../../../../domain/simulation/model/simulationImpact';
import {Amount} from '../../../../../../../domain/shared/amount';
import {AmountFormatterService} from '../../../../../../services/amount-formatter.service';
import {ApplicationName} from '../../../../../../../domain/core/model/aplication-name.enum';
import {UserContextService} from '../../../../../../../application/app-context/user-context/user-context.service';
import {DomainUrlService} from '../../../../../../../application/config/domain-url.service';

@Component({
  selector: 'app-widget-simulation-large',
  templateUrl: './home-dashboard-widget-simulation-large.component.html'
})
export class HomeDashboardWidgetSimulationLargeComponent extends SafeSubscriberDirective implements OnInit {

  @Input() widget: WidgetDetail | undefined;
  @Input() simulationData: SimulationImpact | undefined;
  @Input() dashboardId: number | undefined;
  @Input() hideTitle = false;

  chart: Chart;
  currentSimulationId: string;
  currentSimulationLabel: string;
  public insitoAccountId;
  hasData: boolean | undefined;

  @Output() actionEvent = new EventEmitter<string>();

  constructor(private widgetService: WidgetService,
              private userContextService: UserContextService,
              private domainUrl: DomainUrlService,
              private translateService: TranslateService,
              public amountFormatterService: AmountFormatterService) {
    super();
    const userContext = this.userContextService.getUserContext();
    const currentAccount = userContext.user.accounts.find(account => account.accountId === userContext.accountId);
    this.insitoAccountId = currentAccount.applications.find(application => (application.targetApp === ApplicationName.INSITO) && application.accountSynchId !== null).accountSynchId;
  }

  ngOnInit(): void {
    if (this.simulationData.simulateds && this.simulationData.simulateds.length > 0) {
      this.currentSimulationId = this.simulationData.simulateds[0].simulationId;
      this.currentSimulationLabel = this.simulationData.simulateds[0].simulationLabel;
      this.loadChart();
    }
  }

  previousSimulation(): void {
    const currentIndex = this.simulationData.simulateds.map(d => d.simulationId).indexOf(this.currentSimulationId);
    this.currentSimulationId = this.simulationData.simulateds[currentIndex - 1].simulationId;
    this.currentSimulationLabel = this.simulationData.simulateds[currentIndex - 1].simulationLabel;
    this.loadChart();
    this.actionEvent.emit('widget.amplitude.carousel-use');
  }

  nextSimulation(): void {
    const currentIndex = this.simulationData.simulateds.map(d => d.simulationId).indexOf(this.currentSimulationId);
    this.currentSimulationId = this.simulationData.simulateds[currentIndex + 1].simulationId;
    this.currentSimulationLabel = this.simulationData.simulateds[currentIndex + 1].simulationLabel;
    this.loadChart();
    this.actionEvent.emit('widget.amplitude.carousel-use');
  }

  public trackAction(actionKey: string): void {
    this.actionEvent.emit(actionKey);
  }

  isLastSimulation(): boolean {
    return this.simulationData.simulateds.map(d => d.simulationId).indexOf(this.currentSimulationId) === this.simulationData.simulateds.length - 1;
  }

  isFirstSimulation(): boolean {
    return this.simulationData.simulateds.map(d => d.simulationId).indexOf(this.currentSimulationId) === 0;
  }

  redirectToInsito(): string {
    return this.simulationData.simulateds && this.simulationData.simulateds.length > 0 ?
      `${this.domainUrl.getInsitoUrl()}?accountId=${this.insitoAccountId}&redirectLink=tableau_de_bord_link&simulationIdToAdd=` + this.currentSimulationId :
      `${this.domainUrl.getInsitoUrl()}?accountId=${this.insitoAccountId}&redirectLink=simulation_link`;
  }

  private loadChart(): void {
    const amountFormatter = this.amountFormatterService;
    const formatValue = this.formatValue;
    const spec = {
      credits: {
        enabled: false
      },
      chart: {
        type: 'column',
        spacing: [1, 1, 1, 1],
        marginTop: 2,
        spacingTop: 2,
        spacingBottom: 0,
        marginBottom: 10,
        plotBorderColor: '#6a7480',
        backgroundColor: '#ffffff',
        plotBorderWidth: 0,
        style: {
          fontFamily: 'Arial, Helvetica, sans-serif',
          'font-family': 'Arial, Helvetica, sans-serif'
        }
      },
      colors: ['#1c3966', '#e95053', '#f59700', '#008CBA', '#9F1BB7'],
      title: {
        text: null
      },
      subtitle: {
        text: null
      },
      exporting: {
        enabled: false,
      },
      accessibility: {
        announceNewData: {
          enabled: true
        }
      },
      xAxis: {
        categories: this.getListYearsToDisplay(),
        title: {
          text: null
        },
        type: 'category',
        gridLineColor: '#CCCCCC',
        gridLineDashStyle: 'Dash',
        gridLineWidth: 0,
        lineColor: '#CCCCCC',
        tickPosition: 'inside',
        tickmarkPlacement: 'on',
        tickWidth: 0,
        tickColor: '#CCCCCC',
        labels: {
          enabled: true,
          padding: 1,
          y: 9,
          style: {
            color: '#000000',
            fontSize: '9px',
            fontWeight: 'normal'
          }
        },
        style: {
          fontFamily: 'Arial, Helvetica, sans-serif',
          'font-family': 'Arial, Helvetica, sans-serif'
        }
      },
      yAxis: {
        min: 0,
        className: 'widget-highcharts-yaxis',
        title: {
          text: null
        },
        gridLineColor: '#CCCCCC',
        gridLineDashStyle: 'Dash',
        gridLineWidth: 1,
        lineColor: '#CCCCCC',
        lineWidth: 1,
        labels: {
          x: -8,
          formatter(): string {
            // @ts-ignore
            return formatValue(new Amount(this.value, '€', false), amountFormatter);
          },
          style: {
            color: '#37404E',
            fontSize: '9px',
            fontWeight: 'normal'
          }
        },
        tickPosition: 'inside',
        tickWidth: 0,
        tickLength: 0,
        tickColor: '#6a7480',
        tickInterval: 5,
        style: {
          fontFamily: 'Arial, Helvetica, sans-serif',
          'font-family': 'Arial, Helvetica, sans-serif'
        }
      },
      legend: {
        enabled: false,
        itemStyle: {
          lineHeight: '12px',
          fontSize: '10px',
          color: '#6A7480'
        },
        style: {
          fontFamily: 'Arial, Helvetica, sans-serif',
          'font-family': 'Arial, Helvetica, sans-serif'
        }
      },
      plotOptions: {
        column: {
          stacking: 'normal',
          borderWidth: 0,
          maxPointWidth: 15,
          pointWidth: 15,
          pointPadding: 0.2,
          dataLabels: {
            style: {
              color: '#ffffff',
              fontWeight: 'normal',
              fontSize: '10px',
              textOutline: 'none'
            },
            enabled: false,
            shadow: false,
            format: '{point.formattedAmount}'
          }
        }
      },
      tooltip: {
        style: {
          color: '#333333',
          cursor: 'default',
          fontSize: '12px',
          pointerEvents: 'none',
          whiteSpace: 'nowrap'
        },
        formatter(): string {
          const interestBefore = this.points.find(p => p.series.index === 0);
          const amortizationBefore = this.points.find(p => p.series.index === 1);
          const interestAfter = this.points.find(p => p.series.index === 2);
          const amortizationAfter = this.points.find(p => p.series.index === 3);
          let s = '';
          s += this.points[0].key + '<br/>';
          s += '<span style="fill:' + amortizationBefore.point.color + '">' + amortizationBefore.series.userOptions.label + '</span>: <b></br></br>';
          s += '● <span style="fill:' + amortizationBefore.point.color + '">' + this.points.find(p => p.series.index === 0).series.userOptions.stackLabel +
            ': </span><b>' + amortizationBefore.point.formattedAmountLong + '</b></br>';
          s += '● <span style="fill:' + amortizationAfter.point.color + '">' + this.points.find(p => p.series.index === 2).series.userOptions.stackLabel +
            ': </span><b>' + amortizationAfter.point.formattedAmountLong + '</b></br>';
          s += '<span style="fill:' + interestBefore.point.color + '">' + interestBefore.series.userOptions.label + '</span>: <b></br></br>';
          s += '● <span style="fill:' + interestBefore.point.color + '">' + this.points.find(p => p.series.index === 0).series.userOptions.stackLabel +
            ': </span><b>' + interestBefore.point.formattedAmountLong + '</b></br>';
          s += '● <span style="fill:' + interestAfter.point.color + '">' + this.points.find(p => p.series.index === 2).series.userOptions.stackLabel +
            ': </span><b>' + interestAfter.point.formattedAmountLong + '</b></br>';
          return s;
        },
        shared: true,
        outside: true
      },
      series: this.getSeries()

    };
    this.chart = new Chart(spec as Highcharts.Options);
  }

  private formatValue(amount: Amount, amountFormatterService: AmountFormatterService): string {
    return amountFormatterService.format(amount, '0.0a');
  }

  private getSeries(): any {
    return [{
      type: 'column',
      name: 'INTEREST',
      label: this.translateService.instant('widget.simulation.interest'),
      data: this.simulationData.actualByDates.map(this.buildDataForInterest()),
      borderRadiusTopLeft: 5,
      borderRadiusTopRight: 5,
      stack: 'BEFORE',
      stackLabel: this.translateService.instant('widget.simulation.before'),
      color: '#E95053'
    }, {
      type: 'column',
      name: 'AMORTIZATION',
      label: this.translateService.instant('widget.simulation.amortization'),
      data: this.simulationData.actualByDates.map(this.buildDataForAmortization()),
      stack: 'BEFORE',
      stackLabel: this.translateService.instant('widget.simulation.before'),
      color: '#1BB7B7'
    }, {
      type: 'column',
      name: 'INTEREST',
      label: this.translateService.instant('widget.simulation.interest'),
      data: this.simulationData.simulateds.find(s => s.simulationId === this.currentSimulationId).profilExtinctionIndicatorsByYears.map(this.buildDataForInterest()),
      color: '#B41C1F',
      borderRadiusTopLeft: 5,
      borderRadiusTopRight: 5,
      stack: 'AFTER',
      stackLabel: this.translateService.instant('widget.simulation.after'),
    }, {
      type: 'column',
      name: 'AMORTIZATION',
      label: this.translateService.instant('widget.simulation.amortization'),
      data: this.simulationData.simulateds.find(s => s.simulationId === this.currentSimulationId).profilExtinctionIndicatorsByYears.map(this.buildDataForAmortization()),
      color: '#139A9A',
      stack: 'AFTER',
      stackLabel: this.translateService.instant('widget.simulation.after'),
    }];
  }

  private buildDataForInterest(): any {
    return a => ({
      y: a.profilExtinctionIndicators.interest,
      formattedAmount: this.amountFormatterService.format(new Amount(a.profilExtinctionIndicators.interest, '€', false), '0.0a'),
      formattedAmountLong: this.amountFormatterService.format(new Amount(a.profilExtinctionIndicators.interest, '€', false), '0,0')
    });
  }

  private buildDataForAmortization(): any{
    return a => ({
      y: a.profilExtinctionIndicators.amortization,
      formattedAmount: this.amountFormatterService.format(new Amount(a.profilExtinctionIndicators.amortization, '€', false), '0.0a'),
      formattedAmountLong: this.amountFormatterService.format(new Amount(a.profilExtinctionIndicators.amortization, '€', false), '0,0')
    });
  }

  private getListYearsToDisplay(): string[] {
    return this.simulationData.actualByDates.map(a => a.year);
  }
}
